import {
  inject,
  computed,
  getCurrentInstance,
  toValue,
  reactive,
  provide,
  ref,
  watch,
  type ComponentPublicInstance,
  type InjectionKey,
  type Ref,
  type ComputedRef,
  type WritableComputedRef,
  type ShallowRef,
  useTemplateRef,
} from 'vue';
import { asyncComputedList, type AsyncComputedStateObject } from '@common/asyncComputed';
import { useChildrenTracker, provideChildrenTracker, useResettableState, type ChildrenTrackerInjectionKey } from '@common/use';
import { by, locations } from '@common/utils';
import { $t } from '@/i18n';
import { type NormalizedCatalogProduct } from '@/pages/catalog.vue';
import { useLoginStore } from '@/store/login';
import { useAlertStore } from '@/store/alerts';
import { useCatalogStore } from '@/store/catalog';
import { useRouter } from 'vue-router';
import { REGION, PRODOTTIORDINI_PROVISIONDB, PRODOTTIORDINI_PROVISIONPHP, PRODOTTIANAGRAFICA_DURATA, PRODUCTCATEGORY } from '@common/consts';
import {
  MyCustomersResource,
  WizardOfferResource,
  CompanyResource,
  OffersResource,
  type OfferUpdate,
  type WizardAction,
  type ProvisionInfo,
  type Offer,
  type OfferTotals,
  type MyCustomersAccess,
  type CustomOfferNotes,
} from '@/resources';
import type PfFlex from '@vue-patternfly/core/layouts/Flex/Flex.vue';
import modalStyles from '@patternfly/react-styles/css/components/ModalBox/modal-box';

export type NonEmptyArray<T> = [T, ...T[]];
export type Product = NormalizedCatalogProduct &
  ProvisionInfo & {
    codice_wizard: string;
  };
export type ProductPriceInfo = Pick<Product, 'nome' | 'prezzo' | 'prezzo_scontato' | 'durata' | 'utenti' | 'categoria'>;

export type OpzioniStack =
  | 'essential'
  | 'essential windows'
  | 'cockpit'
  | 'whm'
  | 'plesk linux'
  | 'plesk windows'
  | 'windows managed'
  | 'server mail dedicato'
  | 'mailarchive';

export type StackInfo = Record<
  OpzioniStack,
  {
    codici: NonEmptyArray<string>;
    descrizione: string;
    minCpu: number;
    minRam: number;
    minStorage: number;
    os: OperativeSystems[];
  }
>;

export type InfoSaveWizard = {
  title: string;
  note?: string;
  prodotti: ProvisionInfo[];
  id_offerta?: number;
};

export interface Path {
  label: string;
  description: string;
  initialStep: string;
}

export interface Paths {
  hosting?: Path;
  cloud?: Path;
  staging?: Path;
  host?: Path;
  dominio?: Path;
  mail?: Path;
  storage?: Path;
  altro?: Path;
}

export type OperativeSystems = 'linux' | 'windows';
export interface ProductCodesByInfraType {
  hosting: string[];
  mail: string[];
  host: string[];
  storage: string[];
}

export interface CodiciCloud {
  cpu: string;
  ram: string;
  storage: string;
}

export type CaratteristicheCloud = {
  cpu: number;
  ram: number;
  storage: number;
};

export const opzioniWebServers = ['NGINX', 'Apache'] as const;
export const opzioniTecnologieHosting = ['WordPress', 'Joomla', 'Drupal'];
export const opzioniTecnologieCloud = {
  cms: ['WordPress', 'Joomla', 'Drupal'],
  ecommerce: ['Magento', 'PrestaShop', 'Shopware', 'OroCommerce', 'WooCommerce'],
  'power app': ['Nextcloud', 'Odoo', 'Open WebUI', 'n8n'],
  container: ['Docker', 'Podman'],
  linguaggi: ['Node.js', 'Python', 'PHP', '.Net'],
};

export const codiciProdottiInfrastruttura: ProductCodesByInfraType = {
  hosting: ['HOSMN', 'HOSMRS', 'HOSVEN', 'HOSERT', 'HOSTITA'],
  mail: ['MLART10', 'MLART20', 'MLART40', 'MLART60', 'MLART80', 'MLART100', 'MLART120'],
  host: ['HOSTCORE', 'HOSTGEN'],
  storage: ['STOS3', 'STOSMART', 'MLARCH100'],
};

export const codiciCloud: CodiciCloud = {
  cpu: 'MAVCPU',
  ram: 'MAVRAM',
  storage: 'MAVSTO',
};

export const cpuFirewallQt = 2;
export const ramFirewallQt = 2;
export const storageFirewallQt = 40;

export const codiceProdottoStaging = 'HOSRES';
export const codiceProdottoBackup = 'STBCKACRMO';
export const codiceProdottoSpazioBackup = 'STBCKACRSWMO';

export const codiciProdottiAddon = {
  registrazionePremium: 'REGPREMY',
  ssl: ['SSLBAS', 'SSLART', 'SSLPREMY', 'SSLWLD'],
  imunify360: ['AHSIM360250', 'AHSIM360500'],
  liteSpeed: 'AHSLITE',
  winServer: 'CLDWINSTD',
  terminalServer: 'CLDRD',
  atlas: 'MLARCH100MO',
  supplierFirewall: 'CLOUDAPPVPN',
  sla: 'SLAPRMC',
  addOn8Ip: 'CLDADD8IP',
  addOn32Ram: 'CLDADD32RAM',
  addOnStorageHost: 'STOCDSMO',
  storageCloud: ['STOSMART', 'STOS3'],
  extraTimeTransferProduct: 'TONEETTRANS',
};
export const codiciProdottiSql = ['CLDSQW', 'CLDSQS', 'CLDSQE'] as const;

type ComponentExposedPropBackend<T> = T | Ref<T> | ShallowRef<T> | ComputedRef<T> | WritableComputedRef<T>;

type ComponentExposed<Type> = {
  [K in keyof Type]: ComponentExposedPropBackend<Type[K]>;
};

export type WizardInjection = {
  readonly isFirstIteration: boolean;
  readonly opzioniProdottiInfrastruttura: string[];
  readonly codiciProdottiInfrastruttura: ProductCodesByInfraType;
  readonly opzioniWebServers: typeof opzioniWebServers;
  readonly cloudOperativeSystems: string[];
  readonly hostingPaths: Paths;
  readonly cloudPaths: Paths;
  readonly resellerPaths: Paths;
  readonly onHostingPath: boolean;
  readonly codiceProdottoDominio: string;
  readonly codiciProdottiAddon: typeof codiciProdottiAddon;
  readonly codiceProdottoStaging: string;
  readonly codiceProdottoBackup: string;
  readonly codiceProdottoSpazioBackup: string;
  readonly codiciProdottiSql: typeof codiciProdottiSql;
  readonly stacks: StackInfo;
  readonly codiciCloud: CodiciCloud;
  readonly codiceProdottoImunify: string;
  readonly dominioRegistrabile: boolean;
  readonly sottodominio: boolean;
  readonly customers: MyCustomersAccess[];
  readonly customer: MyCustomersAccess | undefined;
  readonly askClienti: boolean;
  readonly modal: boolean;
  readonly modalBoxTitle: ShallowRef<HTMLSpanElement | null>;
  readonly modalFooter: ShallowRef<ComponentExposed<typeof PfFlex> | null>;
  readonly askStack: boolean;
  readonly askFirewall: boolean;
  readonly askNomeServer: boolean;
  readonly askSsl: boolean;
  readonly askAddonStorage: boolean;
  readonly askAddonCpanel: boolean;
  readonly askAddonWindows: boolean;
  readonly askMailAddon: boolean;
  readonly askOs: boolean;
  readonly askEssentialOs: boolean;
  readonly askCockpitConfig: boolean;
  readonly askTecnologie: boolean;
  readonly askConfigurazione: boolean;
  readonly askDb: boolean;
  readonly askPhp: boolean;
  readonly askDomainFirst: boolean;
  readonly askDomainAddon: boolean;
  readonly domainRequired: boolean;
  readonly domainRegistrationRequired: boolean;
  readonly askRegion: boolean;
  readonly askRegionBackup: boolean;
  readonly askProdotti: boolean;
  readonly askStorageQt: boolean;
  readonly askDurataFatturazione: boolean;
  readonly askTest: boolean;
  readonly askSiteTransfer: boolean;
  readonly askMailTransfer: boolean;
  readonly askExtraTimeTransfer: boolean;
  readonly hidePlusVat: boolean;
  readonly locations: typeof locations;
  readonly groupedProducts: Record<string, Product[]>;
  readonly defaultTitle: string;
  readonly canConfirmOffer: boolean;
  readonly durata: (typeof PRODOTTIANAGRAFICA_DURATA)[number];
  readonly serverCloud: ProductPriceInfo;
  readonly firewall: ProductPriceInfo;
  readonly cpuFirewallQt: number;
  readonly ramFirewallQt: number;
  readonly storageFirewallQt: number;
  readonly serverCloudName: string;
  readonly firewallName: string;
  readonly configurazione: string | null;
  readonly totaliWizard: OfferTotals;
  readonly standardClouds: CaratteristicheCloud[];
  readonly customOfferNotes: CustomOfferNotes[];
  readonly availableDomain: boolean | null | undefined;
  readonly searchedDomain: string | null | undefined;
  readonly initialStep: string;
  readonly progressSteps: string[];
  currentStep: string;
  freePeriod: boolean;
  notForResale: boolean;
  opzioniTecnologieHosting: string[];
  opzioniTecnologieCloud: Record<string, string[]>;
  db: (typeof PRODOTTIORDINI_PROVISIONDB)[number] | undefined;
  php: (typeof PRODOTTIORDINI_PROVISIONPHP)[number] | undefined;
  path: keyof Paths | undefined;
  sceltaLocationDeploy: (typeof REGION)[number] | undefined;
  sceltaLocationBackup: (typeof REGION)[number] | undefined;
  codiceProdottoStack: string | undefined;
  fatturaMe: boolean;
  askAddonSla: boolean;
  addonSla: boolean;
  addonLiteSpeed: boolean;
  addonImunify: boolean;
  addonTerminalServer: boolean;
  terminalServerQt: number;
  addonAtlas: boolean;
  addOnFirewall: boolean;
  addOn8Ip: boolean;
  addOn32Ram: boolean;
  addOn8IpQt: number;
  addOn32RamQt: number;
  addHostWin: boolean;
  addonStorage: boolean; // Storage aggiuntivo dell'host server
  codiceStorageCloud: string; // Storage aggiuntivo del cloud server, vuoto se non richiesto
  qtAddonStorage: number; // Quantità dello storage aggiuntivo, sia host che cloud
  attivaDominio: boolean;
  registrazionePremium: boolean;
  mailAddon: boolean;
  idClienteFinale: number | undefined;
  codiceSsl: string;
  codiceProdottoMail: string;
  codiceLicenzaSql: (typeof codiciProdottiSql)[number] | undefined;
  stack: OpzioniStack | null | undefined;
  os: OperativeSystems;
  essentialOs: string | undefined;
  essentialOtherOs: string | undefined;
  cockpitOs: string | undefined;
  cockpitWs: (typeof opzioniWebServers)[number] | undefined;
  sceltaProdottoInfrastruttura: string;
  nomeServer: string;
  cpuCloudQt: number;
  ramCloudQt: number;
  storageCloudQt: number;
  storageQt: number;
  nomeDominio: string;
  fatturazioneAnnuale: boolean;
  renewAfterFreePeriod: boolean;
  tecnologie: string[];
  newService: boolean | undefined;
  siteTransfer: boolean | undefined;
  mailTransfer: boolean | undefined;
  extraTimeTransfer: boolean;
  action: WizardAction | 'continue' | 'save' | undefined;
  offer: Offer | null;
  cashback: number;
  title: string | undefined;
  note: string | undefined;
  customNote: string[];
  sending: boolean;
  getProdotto: (codici: string) => Product;
  getProdotti: (codici: readonly string[]) => Product[];
  tldRegistrabile: (domain: string) => string | null;
  totaleSingoloWizardByDurata: (durata: (typeof PRODOTTIANAGRAFICA_DURATA)[number], scontato: boolean, group?: string) => number;
  minPrice: (codici: string[]) => Product;
  attiva: (value: boolean) => string;
  siNo: (value: boolean) => string;
  submit: (action: WizardAction | 'continue' | 'save') => Promise<void>;
  confirmOffer: () => Promise<void>;
  removeProducts: (domain: string) => void;
  nextStep: () => void;
  previousStep: () => void;
  resetWizard: (dontAsk?: boolean) => void;
  otherIteration: () => void;
  customersState: AsyncComputedStateObject;
};

export const WizardInjectionKey = Symbol('WizardInjectionKey') as InjectionKey<WizardInjection>;

const DRAFT_OFFER_INITIAL_STEP = 'WizardStepScopo';
const SUMMARY_STEP = 'WizardStepSummary';
const THANKYOU_STEP = 'WizardStepThankYou';

type StepData = ReturnType<typeof useWizardStep>;
type StepComponent = ComponentPublicInstance<object, object, StepData>;

export const WizardStepsTrackerSymbol: ChildrenTrackerInjectionKey<StepComponent> = Symbol('Wizard steps tracker symbol');

export function useWizard() {
  const wizard = inject(WizardInjectionKey);
  if (!wizard) {
    throw new Error('WizardStep component must be used inside Wizard component');
  }
  return wizard;
}

export function useWizardStep({ isEnabled = (() => true) as (wizard: WizardInjection) => boolean } = {}) {
  const instance = getCurrentInstance()?.proxy;
  useChildrenTracker(WizardStepsTrackerSymbol);
  const wizard = useWizard();

  return {
    wizard,
    hidden: computed(() => wizard.currentStep !== instance?.$options.name),
    isEnabled: computed(() => wizard && isEnabled(wizard)),
  };
}

export function useWizardQuestion() {
  return {
    wizard: useWizard(),
  };
}

export function useWizardSetup({
  modal = false,
  searchedDomain = null as string | null,
  availableDomain = null as boolean | null,
  singlePath = null as keyof Paths | null,
} = {}) {
  const login = useLoginStore();
  const alert = useAlertStore();
  const catalog = useCatalogStore();
  const steps = provideChildrenTracker(WizardStepsTrackerSymbol);
  const offer: Ref<Offer | null> = ref(null);
  const router = useRouter();

  const standardClouds: CaratteristicheCloud[] = [
    {
      cpu: 1,
      ram: 1,
      storage: 20,
    },
    {
      cpu: 2,
      ram: 2,
      storage: 40,
    },
    {
      cpu: 4,
      ram: 4,
      storage: 80,
    },
    {
      cpu: 4,
      ram: 8,
      storage: 160,
    },
    {
      cpu: 8,
      ram: 16,
      storage: 320,
    },
    {
      cpu: 12,
      ram: 24,
      storage: 400,
    },
    {
      cpu: 16,
      ram: 32,
      storage: 600,
    },
  ];

  const stacks: StackInfo = {
    essential: {
      codici: ['AHSESS'],
      descrizione: $t(
        'Questo stack include il monitoraggio essenziale dei parametri vitali e i backup di sicurezza giornalieri, assicurandoti tranquillità e prestazioni stabili per le tue applicazioni.',
      ),
      minCpu: 1,
      minRam: 1,
      minStorage: 20,
      os: ['linux'],
    },
    'essential windows': {
      codici: ['AHSESS'],
      descrizione: $t(
        'Questo stack include il monitoraggio essenziale dei parametri vitali e i backup di sicurezza giornalieri, assicurandoti tranquillità e prestazioni stabili per le tue applicazioni.',
      ),
      minCpu: 1,
      minRam: 1,
      minStorage: 50,
      os: ['windows'],
    },
    cockpit: {
      codici: ['AHSCCKP'],
      descrizione: $t(
        'Con NGINX, Percona Server for MySQL, Redis e il pannello Cockpit, questo stack offre prestazioni elevate e una gestione semplificata. Include sonde avanzate per un monitoraggio proattivo e gestione personalizzata dei backup, garantendo massima sicurezza e reattività.',
      ),
      minCpu: 1,
      minRam: 1,
      minStorage: 40,
      os: ['linux'],
    },
    whm: {
      codici: ['AHSWHM1', 'AHSWHM250', 'AHSWHM500'],
      descrizione: $t(
        "Perfetto per l'hosting reseller, combina CloudLinux OS e WHM / cPanel per una gestione avanzata. Include supporto specialistico e tutte le funzionalità avanzate del cloud hosting Artera, rendendolo ideale per chi cerca una soluzione completa e affidabile.",
      ),
      minCpu: 4,
      minRam: 8,
      minStorage: 100,
      os: ['linux'],
    },
    'plesk linux': {
      codici: ['AHSPLK1', 'AHSPLK30', 'AHSPLK500'],
      descrizione: $t(
        'Questo stack fornisce un pannello di controllo intuitivo con Plesk per Linux, ottimizzato per la gestione di siti web, applicazioni e server email. Offre strumenti avanzati e supporto specialistico, ideale per professionisti e agenzie che desiderano efficienza.',
      ),
      minCpu: 1,
      minRam: 2,
      minStorage: 40,
      os: ['linux'],
    },
    'windows managed': {
      codici: ['AHSWIN'],
      descrizione: $t('Questo stack ti permette di avere il tuo server Windows completamente gestito.'),
      minCpu: 1,
      minRam: 4,
      minStorage: 50,
      os: ['windows'],
    },
    'plesk windows': {
      codici: ['AHSPLKW1', 'AHSPLKW30', 'AHSPLKW500'],
      descrizione: $t(
        'Questo stack fornisce un pannello di controllo intuitivo con Plesk per Windows, ottimizzato per la gestione di siti web, applicazioni e server email. Offre strumenti avanzati e supporto specialistico, ideale per professionisti e agenzie che desiderano efficienza.',
      ),
      minCpu: 1,
      minRam: 4,
      minStorage: 50,
      os: ['windows'],
    },
    'server mail dedicato': {
      codici: ['AHSDEDMAILY'],
      descrizione: $t('Questo stack ti permette di attivare un server mail dedicato. Attiveremo una licenza WHM dedicata per 5 utenti'),
      minCpu: 2,
      minRam: 4,
      minStorage: 40,
      os: ['linux'],
    },
    mailarchive: {
      codici: ['AHSATLAS'],
      descrizione: $t(
        'Attiva un server di archiviazione email privato con un potente motore di ricerca. Atlas Mail Archive è perfetto per archiviare e recuperare facilmente tutte le email aziendali, garantendo sicurezza e conformità normativa nel flusso di comunicazioni.',
      ),
      minCpu: 1,
      minRam: 1,
      minStorage: 40,
      os: ['linux'],
    },
  };

  const hostingPaths = {
    dominio: {
      label: $t('Registrazione dominio e DNS'),
      description: $t(
        "Registra il tuo dominio tra oltre 200 estensioni TLD, con DNS avanzato e redirect personalizzati. Un servizio sicuro e flessibile per garantire un'identità digitale stabile e sempre accessibile, con fatturazione annuale.",
      ),
      initialStep: 'WizardStepDominio',
    },
    mail: {
      label: $t('Solo email (Include la possibilità di registrare un dominio)'),
      description: $t(
        'Affidati a Only Mail per una gestione email sicura e performante. Caselle illimitate, spazio dedicato e backup automatici assicurano continuità operativa e protezione avanzata per la tua comunicazione aziendale.',
      ),
      initialStep: 'WizardStepDominio',
    },
    hosting: {
      label: $t('Hosting (Include la possibilità di attivare un dominio e il servizio email)'),
      description: $t(
        'Hosting potente e affidabile con email integrate, backup automatico e certificato SSL. Scegli Linux con LiteSpeed per massima velocità o Windows con Plesk per compatibilità estesa. Configura il tuo piano e lancia il tuo progetto in sicurezza.',
      ),
      initialStep: 'WizardStepDominio',
    },
  };

  const cloudPaths = {
    cloud: {
      label: $t('Cloud server'),
      description: $t(
        "Configura il tuo cloud server dedicato per siti web, e-commerce e applicazioni. Un'infrastruttura scalabile e ottimizzata sulle tue esigenze, con massima flessibilità operativa e sicurezza avanzata.",
      ),
      initialStep: 'WizardStepProdottiInfrastruttura',
    },
    host: {
      label: $t('Host server (Server dedicato)'),
      description: $t(
        "Affidati a un'infrastruttura hardware dedicata e performante per applicazioni aziendali e servizi business-critical. Risorse esclusive, sicurezza avanzata e scalabilità garantiscono continuità operativa e massima affidabilità con storage all-flash, firewall e VPN personalizzate.",
      ),
      initialStep: 'WizardStepProdottiInfrastruttura',
    },
    storage: {
      label: $t('Storage & Backup'),
      description: $t(
        'Attiva subito spazio di archiviazione sicuro e ridondato per proteggere i tuoi dati. Soluzioni flessibili per backup gestito, storage cloud e archiviazione di file critici, con massima affidabilità e recupero rapido.',
      ),
      initialStep: 'WizardStepProdottiInfrastruttura',
    },
    altro: {
      label: $t('Altro'),
      description: $t(
        'Licenze software, certificati SSL, archiviazione email Atlas e altre soluzioni professionali per migliorare e proteggere la tua infrastruttura digitale.',
      ),
      initialStep: 'WizardStepAddon',
    },
  };

  const resellerPaths = {
    staging: {
      label: $t('Staging Hosting'),
      description: $t(
        'Ottimizza la gestione dei tuoi staging con una soluzione reseller potente e flessibile. Domini e sottodomini illimitati, gestione completamente autonoma, pannello reseller completo. Non include migrazioni o supporto applicativo. Ideale per web agency, sviluppatori con clienti multipli o chiunque cerchi un hosting indipendente a costo contenuto.',
      ),
      initialStep: 'WizardStepDominio',
    },
  };

  const paths: Paths = {
    ...hostingPaths,
    ...cloudPaths,
    ...resellerPaths,
  };

  // Stato di caricamento del wizard
  const ready = computed(() => catalog.state === 'success' && priceListState.state === 'success' && !deletingProducts.value);
  const sending = ref(false);
  const deletingProducts = ref(false);

  const isFirstIteration = computed(() => !offer.value);
  const initialStep = computed(() => (isFirstIteration.value ? newOfferInitialStep : DRAFT_OFFER_INITIAL_STEP));
  const askClienti = computed(() => login.is_reseller);
  const modalBoxTitle = useTemplateRef<HTMLSpanElement>('modalBoxTitleRef');
  const modalFooter = useTemplateRef<ComponentExposed<typeof PfFlex>>('modalFooterRef');

  const newOfferInitialStep = singlePath
    ? (paths[singlePath]?.initialStep ?? (askClienti.value ? 'WizardStepClienti' : 'WizardStepScopo'))
    : askClienti.value
      ? 'WizardStepClienti'
      : 'WizardStepScopo';

  const durata: ComputedRef<(typeof PRODOTTIANAGRAFICA_DURATA)[number]> = computed(() => (resettableState.fatturazioneAnnuale.value ? 'Annuale' : 'Mensile'));

  const askOs = computed(() => resettableState.path.value === 'cloud');

  const askEssentialOs = computed(() => askStack.value && resettableState.stack.value === 'essential' && resettableState.os.value === 'linux');

  const askCockpitConfig = computed(() => askStack.value && resettableState.stack.value === 'cockpit');

  const askRegion = computed(() => resettableState.path.value !== 'dominio' && resettableState.path.value !== 'altro');

  const askRegionBackup = computed(() => !['dominio', 'altro', 'storage'].includes(resettableState.path.value ?? ''));

  const askProdotti = computed(() => !['dominio', 'altro', 'staging'].includes(resettableState.path.value ?? ''));

  const askStorageQt = computed(() => resettableState.path.value === 'storage');

  const askDomainFirst = computed(() => (resettableState.path.value ? ['dominio', 'hosting', 'mail', 'staging'].includes(resettableState.path.value) : false));

  const askDomainAddon = computed(() => !askDomainFirst.value && resettableState.path.value === 'cloud');

  const askMailAddon = computed(
    () =>
      (resettableState.path.value === 'cloud' &&
        (!resettableState.stack.value || ['essential', 'cockpit', 'windows managed'].includes(resettableState.stack.value))) ||
      (resettableState.path.value === 'hosting' && resettableState.os.value === 'windows'),
  );

  const domainRequired = computed(
    () =>
      askDomainFirst.value ||
      resettableState.path.value === 'altro' ||
      resettableState.path.value === 'storage' ||
      (askDomainAddon.value && resettableState.attivaDominio.value) ||
      (askMailAddon.value && resettableState.mailAddon.value) ||
      (searchedDomain && searchedDomain === resettableState.nomeDominio.value) ||
      resettableState.attivaDominio.value,
  );

  const domainRegistrationRequired = computed(
    () =>
      dominioRegistrabile.value &&
      (resettableState.path.value === 'dominio' || ((askDomainFirst.value || askDomainAddon.value) && resettableState.attivaDominio.value)),
  );

  const askSsl = computed(
    () =>
      resettableState.path.value !== 'dominio' &&
      resettableState.path.value !== 'staging' &&
      (resettableState.path.value !== 'cloud' ||
        (askMailAddon.value && resettableState.mailAddon.value) ||
        (askDomainAddon.value && resettableState.attivaDominio.value)) &&
      resettableState.path.value !== 'host',
  );

  const askAddonCpanel = computed(() => resettableState.path.value === 'cloud' && resettableState.stack.value === 'whm');

  const askAddonWindows = computed(
    () => resettableState.path.value === 'cloud' && resettableState.os.value === 'windows' && resettableState.stack.value !== 'plesk windows',
  );

  const askStack = computed(() => resettableState.path.value === 'cloud');

  const askFirewall = computed(() => (resettableState.path.value === 'cloud' && !modal) || resettableState.path.value === 'host');

  const askNomeServer = computed(() => resettableState.path.value === 'cloud' || resettableState.path.value === 'host');

  const askDb = computed(
    () =>
      resettableState.os.value === 'linux' &&
      (resettableState.path.value === 'hosting' ||
        (askStack.value && ['essential', 'cockpit', 'whm', 'plesk linux'].includes(resettableState.stack.value ?? ''))),
  );

  const askPhp = computed(
    () =>
      resettableState.os.value === 'linux' &&
      (resettableState.path.value === 'hosting' ||
        (askStack.value && ['essential', 'cockpit', 'whm', 'plesk linux'].includes(resettableState.stack.value ?? ''))),
  );

  const askTecnologie = computed(
    () => resettableState.os.value === 'linux' && (resettableState.path.value === 'hosting' || resettableState.path.value === 'cloud'),
  );

  const askConfigurazione = computed(
    () =>
      (askDb.value || askPhp.value || askTecnologie.value) &&
      (resettableState.path.value !== 'cloud' ||
        (resettableState.stack.value && !['essential', 'cockpit'].includes(resettableState.stack.value)) ||
        (resettableState.stack.value === 'essential' && resettableState.essentialOs.value !== '3CX') ||
        (resettableState.stack.value === 'cockpit' && resettableState.cockpitOs.value !== '3CX')),
  );

  const askTest = computed(
    () => login.is_reseller && resettableState.path.value !== 'dominio' && resettableState.path.value !== 'altro' && resettableState.path.value !== 'host',
  );

  const askDurataFatturazione = computed(() => resettableState.path.value !== 'dominio');

  const askSiteTransfer = computed(() => login.is_reseller && resettableState.path.value === 'hosting' && resettableState.os.value === 'linux');

  const askMailTransfer = computed(
    () =>
      login.is_reseller &&
      ((resettableState.path.value === 'hosting' && resettableState.os.value === 'linux') ||
        (resettableState.path.value === 'hosting' && askMailAddon.value && resettableState.mailAddon.value) ||
        resettableState.path.value === 'mail'),
  );

  const askExtraTimeTransfer = computed(
    () =>
      resettableState.newService.value === false &&
      ((askSiteTransfer.value && resettableState.siteTransfer.value) || Boolean(askMailTransfer.value && resettableState.mailTransfer.value)),
  );

  const askAddonSla = computed(() => resettableState.path.value !== 'dominio' && resettableState.path.value !== 'altro');

  const askAddonStorage = computed(() => resettableState.path.value === 'cloud');

  const cloudOperativeSystems = computed(() => {
    const os = ['AlmaLinux 10', 'AlmaLinux 9', 'AlmaLinux 8', 'Debian', 'Ubuntu'];
    if (resettableState.stack.value === 'essential') {
      return os.concat('3CX');
    }
    return os;
  });

  const hidePlusVat = computed(() => customer.value !== undefined && customer.value.vat < 0.01);

  const opzioniProdottiInfrastruttura = computed(() => {
    if (
      !resettableState.path.value ||
      resettableState.path.value === 'dominio' ||
      resettableState.path.value === 'cloud' ||
      resettableState.path.value === 'altro' ||
      resettableState.path.value === 'staging'
    ) {
      return [];
    }

    return codiciProdottiInfrastruttura[resettableState.path.value];
  });

  const tldRegistrabili = computed(() => {
    const tld: Record<string, string> = {};
    const domini = catalog.products.filter(p => p.tld);
    for (const dom of domini) {
      if (dom.tld) {
        tld[dom.tld] = dom.codice;
      }
    }
    return tld;
  });

  function tldRegistrabile(domain: string) {
    for (const tld of Object.keys(tldRegistrabili.value).sort(by(s => s.length, true))) {
      if (domain.endsWith(`.${tld}`)) {
        return tld;
      }
    }
    return null;
  }

  // Un sottodominio ha più di un livello prima del TLD registrabile (es. shop.example.it): non va registrato
  const sottodominio = computed(() => {
    const tld = tldRegistrabile(resettableState.nomeDominio.value);
    return tld !== null && resettableState.nomeDominio.value.slice(0, -(tld.length + 1)).includes('.');
  });

  const dominioRegistrabile = computed(() => tldRegistrabile(resettableState.nomeDominio.value) !== null && !sottodominio.value);

  const codiceProdottoDominio = computed(() => tldRegistrabili.value[estensioneDominio.value] ?? '');

  const estensioneDominio = computed(() => {
    const tld = tldRegistrabile(resettableState.nomeDominio.value);
    if (tld !== null) {
      return tld;
    }

    const v = resettableState.nomeDominio.value.split('.');
    return v[v.length - 1] as string;
  });

  const codiceProdottoImunify = computed(() => {
    switch (resettableState.codiceProdottoStack.value) {
      case 'AHSWHM250':
        return 'AHSIM360250';
      case 'AHSWHM500':
        return 'AHSIM360500';
      default:
        return 'AHSIM3601';
    }
  });

  const canConfirmOffer = computed(() => (resettableState.fatturaMe.value ? login.enable_orders : (customer.value?.enable_orders ?? false)));

  const defaultTitle = computed(() =>
    $t('Offerta per servizi internet - {0}', [domainRequired.value ? resettableState.nomeDominio.value : resettableState.nomeServer.value]),
  );

  const configurazione = computed(() => {
    if (!(askTecnologie.value || askPhp.value || askDb.value)) {
      return null;
    }

    const conf = [];
    if (askPhp.value) {
      conf.push($t('PHP: {0}', [resettableState.php.value ?? $t('Non necessario')]));
    }
    if (askDb.value) {
      conf.push($t('Database: {0}', [resettableState.db.value ?? $t('Non necessario')]));
    }
    if (resettableState.tecnologie.value.length) {
      conf.push($t('Applicativi: {0}', [resettableState.tecnologie.value.join(', ')]));
    }

    return conf.join(' - ');
  });

  const serverCloud = computed<ProductPriceInfo>(() => {
    const prezzo =
      getProdotto(codiciCloud.cpu).prezzo * resettableState.cpuCloudQt.value +
      getProdotto(codiciCloud.ram).prezzo * resettableState.ramCloudQt.value +
      getProdotto(codiciCloud.storage).prezzo * resettableState.storageCloudQt.value +
      (resettableState.os.value === 'windows' ? getProdotto(codiciProdottiAddon.winServer).prezzo * Math.round(resettableState.cpuCloudQt.value / 2) : 0);

    const prezzo_scontato =
      getProdotto(codiciCloud.cpu).prezzo_scontato * resettableState.cpuCloudQt.value +
      getProdotto(codiciCloud.ram).prezzo_scontato * resettableState.ramCloudQt.value +
      getProdotto(codiciCloud.storage).prezzo_scontato * resettableState.storageCloudQt.value +
      (resettableState.os.value === 'windows'
        ? getProdotto(codiciProdottiAddon.winServer).prezzo_scontato * Math.round(resettableState.cpuCloudQt.value / 2)
        : 0);

    return {
      nome: 'Server personalizzato',
      prezzo,
      prezzo_scontato,
      durata: durata.value,
      utenti: null,
      categoria: 'Cloud Server' as (typeof PRODUCTCATEGORY)[number],
    };
  });

  const serverCloudName = computed(() => `${resettableState.nomeServer.value}.artera.farm`);

  const firewall = computed(() => {
    if (resettableState.path.value === 'host') {
      return getProdotto(codiciProdottiAddon.supplierFirewall);
    }
    const prezzo =
      getProdotto(codiciCloud.cpu).prezzo * cpuFirewallQt +
      getProdotto(codiciCloud.ram).prezzo * ramFirewallQt +
      getProdotto(codiciCloud.storage).prezzo * storageFirewallQt +
      getProdotto(stacks.essential.codici[0]).prezzo;

    const prezzo_scontato =
      getProdotto(codiciCloud.cpu).prezzo_scontato * cpuFirewallQt +
      getProdotto(codiciCloud.ram).prezzo_scontato * ramFirewallQt +
      getProdotto(codiciCloud.storage).prezzo_scontato * storageFirewallQt +
      getProdotto(stacks.essential.codici[0]).prezzo_scontato;

    return {
      nome: 'Firewall Cloud',
      prezzo,
      prezzo_scontato,
      durata: durata.value,
      utenti: null,
      categoria: 'Cloud Server' as (typeof PRODUCTCATEGORY)[number],
    };
  });

  const firewallName = computed(() => {
    return `${resettableState.nomeServer.value}-firewall.artera.farm`;
  });

  const products = computed(() => {
    const prodotti: Product[] = [];

    // Prodotti dominio
    if (domainRegistrationRequired.value) {
      prodotti.push({
        ...getProdotto(codiceProdottoDominio.value),
        dominio: resettableState.nomeDominio.value,
      });

      if (resettableState.registrazionePremium.value) {
        prodotti.push({
          ...getProdotto(codiciProdottiAddon.registrazionePremium),
          dominio: resettableState.nomeDominio.value,
        });
      }
    }

    // Prodotti infrastruttura
    if (askProdotti.value && resettableState.path.value === 'storage' && resettableState.sceltaProdottoInfrastruttura.value === codiceProdottoBackup) {
      prodotti.push({
        ...getProdotto(codiceProdottoBackup),
        qt: 1,
        dominio: resettableState.nomeDominio.value,
        provision_region: resettableState.sceltaLocationDeploy.value,
      });
      prodotti.push({
        ...getProdotto(codiceProdottoSpazioBackup),
        qt: 1,
        dominio: resettableState.nomeDominio.value,
        provision_region: resettableState.sceltaLocationDeploy.value,
      });
    } else if (askProdotti.value && resettableState.path.value !== 'cloud' && resettableState.sceltaProdottoInfrastruttura.value) {
      const provisionBackupRegion =
        resettableState.path.value === 'mail'
          ? resettableState.sceltaLocationDeploy.value // I prodotti mailart hanno sempre, per il momento, backup nella stessa location
          : resettableState.path.value === 'storage'
            ? undefined
            : resettableState.sceltaLocationBackup.value;

      const infraProd = {
        ...getProdotto(resettableState.sceltaProdottoInfrastruttura.value),
        qt: askStorageQt.value ? resettableState.storageQt.value : 1,
        dominio: askNomeServer.value ? serverCloudName.value : resettableState.nomeDominio.value,
        provision_db: askDb.value ? resettableState.db.value : undefined,
        provision_php: askPhp.value ? resettableState.php.value : undefined,
        provision_region: resettableState.sceltaLocationDeploy.value,
        provision_backup_region: provisionBackupRegion,
        tecnologie: !askTecnologie.value ? undefined : resettableState.tecnologie.value.length > 0 ? resettableState.tecnologie.value : ['Nessuna'],
      };

      if (askSiteTransfer.value || askMailTransfer.value) {
        infraProd.newService = resettableState.newService.value;

        if (askSiteTransfer.value) {
          infraProd.siteTransfer = resettableState.siteTransfer.value;
        }
        if (askMailTransfer.value) {
          infraProd.mailTransfer = resettableState.mailTransfer.value;
        }
      }

      prodotti.push(infraProd);

      if (askExtraTimeTransfer.value && resettableState.extraTimeTransfer.value) {
        prodotti.push({
          ...getProdotto(codiciProdottiAddon.extraTimeTransferProduct),
          qt: 1,
          dominio: resettableState.nomeDominio.value,
        });
      }
    } else if (askProdotti.value && resettableState.path.value === 'cloud') {
      prodotti.push({
        ...getProdotto(codiciCloud.cpu),
        qt: resettableState.cpuCloudQt.value,
        dominio: serverCloudName.value,
        provision_region: resettableState.sceltaLocationDeploy.value,
        provision_backup_region: resettableState.sceltaLocationBackup.value,
      });
      prodotti.push({
        ...getProdotto(codiciCloud.ram),
        qt: resettableState.ramCloudQt.value,
        dominio: serverCloudName.value,
        provision_region: resettableState.sceltaLocationDeploy.value,
        provision_backup_region: resettableState.sceltaLocationBackup.value,
      });
      prodotti.push({
        ...getProdotto(codiciCloud.storage),
        qt: resettableState.storageCloudQt.value,
        dominio: serverCloudName.value,
        provision_region: resettableState.sceltaLocationDeploy.value,
        provision_backup_region: resettableState.sceltaLocationBackup.value,
      });

      if (resettableState.os.value === 'windows') {
        prodotti.push({
          ...getProdotto(codiciProdottiAddon.winServer),
          dominio: serverCloudName.value,
          qt: Math.round(resettableState.cpuCloudQt.value / 2),
        });
      }

      if (askStack.value && resettableState.codiceProdottoStack.value) {
        const stack = {
          ...getProdotto(resettableState.codiceProdottoStack.value),
          dominio: serverCloudName.value,
        };
        if (askEssentialOs.value) {
          stack.os = resettableState.essentialOs.value === 'altro' ? resettableState.essentialOtherOs.value : resettableState.essentialOs.value;
        } else if (askCockpitConfig.value && resettableState.cockpitWs.value && resettableState.cockpitOs.value) {
          stack.os = resettableState.cockpitOs.value;
          stack.webserver = resettableState.cockpitWs.value;
        } else if (askOs.value) {
          stack.os = resettableState.os.value;
        }
        stack.provision_db = askDb.value ? resettableState.db.value : undefined;
        stack.provision_php = askPhp.value ? resettableState.php.value : undefined;
        stack.tecnologie = !askTecnologie.value ? undefined : resettableState.tecnologie.value.length > 0 ? resettableState.tecnologie.value : ['Nessuna'];
        prodotti.push(stack);
      }

      if (askAddonCpanel.value) {
        if (resettableState.addonImunify.value && codiceProdottoImunify.value) {
          prodotti.push({
            ...getProdotto(codiceProdottoImunify.value),
            dominio: serverCloudName.value,
          });
        }
        if (resettableState.addonLiteSpeed.value) {
          prodotti.push({
            ...getProdotto(codiciProdottiAddon.liteSpeed),
            dominio: serverCloudName.value,
          });
        }
      }

      if (askAddonWindows.value) {
        if (resettableState.addonTerminalServer.value) {
          prodotti.push({
            ...getProdotto(codiciProdottiAddon.terminalServer),
            dominio: serverCloudName.value,
            qt: resettableState.terminalServerQt.value,
          });
        }

        if (resettableState.codiceLicenzaSql.value) {
          prodotti.push({
            ...getProdotto(resettableState.codiceLicenzaSql.value),
            qt: Math.round(resettableState.cpuCloudQt.value / 2),
            dominio: serverCloudName.value,
          });
        }
      }
    } else if (resettableState.path.value === 'staging') {
      prodotti.push({
        ...getProdotto(codiceProdottoStaging),
        dominio: resettableState.nomeDominio.value,
        provision_region: resettableState.sceltaLocationDeploy.value,
        provision_backup_region: resettableState.sceltaLocationBackup.value,
      });
    }

    // Se sto comprando un host devo aggiugnere per forza un cockpit
    if (resettableState.path.value === 'host') {
      prodotti.push({
        ...getProdotto(stacks.cockpit.codici[0]),
        dominio: serverCloudName.value,
        provision_region: resettableState.sceltaLocationDeploy.value,
        provision_backup_region: resettableState.sceltaLocationBackup.value,
      });

      if (resettableState.addOn32Ram.value) {
        prodotti.push({
          ...getProdotto(codiciProdottiAddon.addOn32Ram),
          dominio: serverCloudName.value,
          qt: resettableState.addOn32RamQt.value,
        });
      }
      if (resettableState.addonStorage.value) {
        prodotti.push({
          ...getProdotto(codiciProdottiAddon.addOnStorageHost),
          dominio: serverCloudName.value,
          qt: resettableState.qtAddonStorage.value,
        });
      }
      if (resettableState.addOn8Ip.value) {
        prodotti.push({
          ...getProdotto(codiciProdottiAddon.addOn8Ip),
          dominio: serverCloudName.value,
          qt: resettableState.addOn8IpQt.value,
        });
      }
      if (resettableState.addHostWin.value) {
        prodotti.push({
          ...getProdotto(codiciProdottiAddon.winServer),
          dominio: serverCloudName.value,
          qt: Math.round(Number(getProdotto(resettableState.sceltaProdottoInfrastruttura.value).core) / 2),
        });
      }
    }

    if (askFirewall.value && resettableState.addOnFirewall.value) {
      if (resettableState.path.value === 'host') {
        prodotti.push({
          ...getProdotto(codiciProdottiAddon.supplierFirewall),
          dominio: firewallName.value,
        });
      } else {
        prodotti.push({
          ...getProdotto(codiciCloud.cpu),
          qt: cpuFirewallQt,
          dominio: firewallName.value,
          provision_region: resettableState.sceltaLocationDeploy.value,
          provision_backup_region: resettableState.sceltaLocationBackup.value,
        });
        prodotti.push({
          ...getProdotto(codiciCloud.ram),
          qt: ramFirewallQt,
          dominio: firewallName.value,
          provision_region: resettableState.sceltaLocationDeploy.value,
          provision_backup_region: resettableState.sceltaLocationBackup.value,
        });
        prodotti.push({
          ...getProdotto(codiciCloud.storage),
          qt: storageFirewallQt,
          dominio: firewallName.value,
          provision_region: resettableState.sceltaLocationDeploy.value,
          provision_backup_region: resettableState.sceltaLocationBackup.value,
        });
        prodotti.push({
          ...getProdotto(stacks.essential.codici[0]),
          dominio: firewallName.value,
          os: 'OPNSense',
        });
      }
    }

    if (askAddonStorage.value && resettableState.codiceStorageCloud.value) {
      prodotti.push({
        ...getProdotto(resettableState.codiceStorageCloud.value),
        dominio: serverCloudName.value,
        qt: resettableState.qtAddonStorage.value,
      });
    }

    // Prodotti AddOn
    if (askSsl.value && resettableState.codiceSsl.value) {
      prodotti.push({
        ...getProdotto(resettableState.codiceSsl.value),
        dominio: resettableState.nomeDominio.value,
      }); // Prodotto unicamente annuale
    }
    if (askAddonSla.value && resettableState.addonSla.value) {
      prodotti.push({
        ...getProdotto(codiciProdottiAddon.sla),
        dominio: domainRequired.value && resettableState.nomeDominio.value ? resettableState.nomeDominio.value : serverCloudName.value,
      });
    }

    if (resettableState.path.value === 'altro' && resettableState.addonTerminalServer.value) {
      prodotti.push({
        ...getProdotto(codiciProdottiAddon.terminalServer),
        dominio: resettableState.nomeDominio.value,
        qt: resettableState.terminalServerQt.value,
      });
    }

    if (resettableState.path.value === 'altro' && resettableState.addonAtlas.value) {
      prodotti.push({
        ...getProdotto(codiciProdottiAddon.atlas),
        dominio: resettableState.nomeDominio.value,
      });
    }

    // Prodotti mail
    if (askMailAddon.value && resettableState.mailAddon.value && resettableState.codiceProdottoMail.value) {
      const mailProd = {
        ...getProdotto(resettableState.codiceProdottoMail.value),
        dominio: resettableState.nomeDominio.value,
        provision_region: resettableState.sceltaLocationDeploy.value,
        provision_backup_region: resettableState.sceltaLocationDeploy.value, // I prodotti mailart hanno sempre, per il momento, backup nella stessa location
      };

      if (askMailTransfer.value) {
        mailProd.newService = resettableState.newService.value;
        mailProd.mailTransfer = resettableState.mailTransfer.value;
      }
      prodotti.push(mailProd);
    }

    return prodotti;
  });

  const groupedProducts = computed(() =>
    products.value.reduce<Record<string, Product[]>>((a, p) => {
      const dominio = p.dominio;
      if (!a[dominio]) {
        a[dominio] = [];
      }
      a[dominio].push(p);
      return a;
    }, {}),
  );

  const totaliWizard = computed(() => {
    const totali: Record<(typeof PRODOTTIANAGRAFICA_DURATA)[number], { listino: number; scontato: number }> = {
      Annuale: {
        listino: 0,
        scontato: 0,
      },
      Mensile: {
        listino: 0,
        scontato: 0,
      },
      'Una tantum': {
        listino: 0,
        scontato: 0,
      },
    };

    for (const durata of PRODOTTIANAGRAFICA_DURATA) {
      totali[durata].listino += totaleSingoloWizardByDurata(durata, false);
      totali[durata].scontato += totaleSingoloWizardByDurata(durata, true);
    }

    return totali;
  });

  const initialState: Pick<
    WizardInjection,
    | 'freePeriod'
    | 'notForResale'
    | 'currentStep'
    | 'path'
    | 'tecnologie'
    | 'db'
    | 'php'
    | 'sceltaLocationDeploy'
    | 'sceltaLocationBackup'
    | 'os'
    | 'stack'
    | 'essentialOs'
    | 'essentialOtherOs'
    | 'cockpitOs'
    | 'cockpitWs'
    | 'sceltaProdottoInfrastruttura'
    | 'nomeServer'
    | 'cpuCloudQt'
    | 'ramCloudQt'
    | 'storageCloudQt'
    | 'storageQt'
    | 'attivaDominio'
    | 'nomeDominio'
    | 'registrazionePremium'
    | 'mailAddon'
    | 'codiceProdottoMail'
    | 'codiceLicenzaSql'
    | 'addonLiteSpeed'
    | 'addonImunify'
    | 'addonTerminalServer'
    | 'terminalServerQt'
    | 'addonAtlas'
    | 'addOnFirewall'
    | 'addOn8Ip'
    | 'addOn32Ram'
    | 'addOn8IpQt'
    | 'addOn32RamQt'
    | 'addHostWin'
    | 'addonSla'
    | 'addonStorage'
    | 'codiceStorageCloud'
    | 'qtAddonStorage'
    | 'codiceProdottoStack'
    | 'fatturaMe'
    | 'idClienteFinale'
    | 'renewAfterFreePeriod'
    | 'fatturazioneAnnuale'
    | 'newService'
    | 'siteTransfer'
    | 'mailTransfer'
    | 'extraTimeTransfer'
    | 'action'
    | 'codiceSsl'
    | 'title'
    | 'customNote'
    | 'note'
    | 'cashback'
  > = {
    currentStep: initialStep.value,
    path: singlePath ?? undefined,
    tecnologie: [],
    db: 'best',
    php: 'best',
    sceltaLocationDeploy: login.stato === 'CH' ? 'ch' : 'eu',
    sceltaLocationBackup: login.stato === 'CH' ? 'ch' : 'eu',
    stack: undefined,
    os: 'linux',
    essentialOs: 'AlmaLinux 10',
    essentialOtherOs: undefined,
    cockpitOs: 'AlmaLinux 10',
    cockpitWs: 'NGINX',
    sceltaProdottoInfrastruttura: 'HOSMN',
    nomeServer: '',
    cpuCloudQt: 1,
    ramCloudQt: 1,
    storageCloudQt: 20,
    storageQt: 1,
    attivaDominio: false,
    nomeDominio: searchedDomain ?? '',
    registrazionePremium: false,
    mailAddon: false,
    codiceProdottoMail: '',
    codiceLicenzaSql: undefined,
    addonLiteSpeed: false,
    addonImunify: false,
    addonTerminalServer: false,
    terminalServerQt: 1,
    addonAtlas: false,
    addOnFirewall: false,
    addOn8Ip: false,
    addOn32Ram: false,
    addOn8IpQt: 1,
    addOn32RamQt: 1,
    addHostWin: false,
    addonSla: false,
    addonStorage: false,
    codiceStorageCloud: '',
    qtAddonStorage: 1,
    codiceProdottoStack: undefined,
    fatturazioneAnnuale: true,
    action: undefined,
    newService: undefined,
    siteTransfer: undefined,
    mailTransfer: undefined,
    extraTimeTransfer: false,
    codiceSsl: '',

    // Non resettare a nuova iterazione
    fatturaMe: true,
    idClienteFinale: undefined,
    title: undefined,
    customNote: [],
    note: undefined,
    freePeriod: false,
    notForResale: false,
    cashback: 0,
    renewAfterFreePeriod: true,
  };

  const { state: resettableState, reset: setDefault } = useResettableState(initialState);

  // Scelte che valgono per l'intera offerta e vanno mantenute quando si aggiungono altri prodotti
  const offerState: (keyof typeof initialState)[] = [
    'fatturaMe',
    'idClienteFinale',
    'title',
    'customNote',
    'note',
    'freePeriod',
    'renewAfterFreePeriod',
    'notForResale',
    'cashback',
  ];

  const { state: customersState, result: customers } = asyncComputedList(async () => {
    if (!resettableState.fatturaMe.value) {
      return await new MyCustomersResource().get();
    }
    return [];
  });

  const customer = computed((): MyCustomersAccess | undefined => {
    if (!resettableState.fatturaMe.value && resettableState.idClienteFinale.value) {
      return customers.value.find(c => c.id == resettableState.idClienteFinale.value);
    }

    return {
      id: Number(login.id_azienda),
      ragione_sociale: login.azienda,
      currency: login.currency,
      currency_locked: login.currency_locked,
      vat: login.iva,
      enable_orders: false,
      indirizzo: null,
      provincia: null,
      cap: null,
      citta: null,
      stato: login.stato,
      country_code: login.stato,
      presented: false,
      partita_iva: null,
      contact_name: null,
      contact_mail: null,
      is_reseller: false,
      custom_offer_notes: [],
      has_complete_billing_data: false,
    };
  });

  const { state: priceListState, result: priceList } = asyncComputedList(async () => {
    if (!resettableState.fatturaMe.value && customer.value?.id && customer.value.id !== Number(login.id_azienda)) {
      return await new CompanyResource().priceList(customer.value.id).get();
    }
    return [];
  });

  const { result: companyCustomNotes } = asyncComputedList(async () => {
    if (login.id_azienda) {
      return await new CompanyResource().customOfferNotes(login.id_azienda).get();
    }
    return [];
  });

  const customOfferNotes = computed(() => {
    if (!resettableState.fatturaMe.value && customer.value) {
      return [...(customer.value?.custom_offer_notes ?? []), ...companyCustomNotes.value];
    }
    return companyCustomNotes.value;
  });

  // Controlli sulle opzioni scelte dall'utente
  const onHostingPath = computed(() =>
    Boolean(
      (resettableState.path.value && ['hosting', 'staging'].includes(resettableState.path.value)) ||
      (resettableState.stack.value && ['whm', 'plesk linux'].includes(resettableState.stack.value)),
    ),
  );

  watch(resettableState.stack, s => {
    if (askStack.value && s) {
      // Preseleziona la licenza più piccola (i codici sono ordinati per numero di utenti)
      resettableState.codiceProdottoStack.value = stacks[s].codici[0];
    } else {
      resettableState.codiceProdottoStack.value = undefined;
    }

    if (
      !s ||
      (resettableState.cpuCloudQt.value >= stacks[s].minCpu &&
        resettableState.ramCloudQt.value >= stacks[s].minRam &&
        resettableState.storageCloudQt.value >= stacks[s].minStorage)
    ) {
      return;
    }

    if (resettableState.cpuCloudQt.value < stacks[s].minCpu) {
      resettableState.cpuCloudQt.value = stacks[s].minCpu;
    }
    if (resettableState.ramCloudQt.value < stacks[s].minRam) {
      resettableState.ramCloudQt.value = stacks[s].minRam;
    }
    if (resettableState.storageCloudQt.value < stacks[s].minStorage) {
      resettableState.storageCloudQt.value = stacks[s].minStorage;
    }
    alert.add({ title: $t('Caratteristiche del server aggiornate'), type: 'success', toast: true });
  });

  watch(resettableState.fatturaMe, v => {
    if (v) {
      resettableState.idClienteFinale.value = undefined;
    }
  });

  watch(
    () => customer.value?.country_code,
    v => {
      if (v === 'CH') {
        resettableState.sceltaLocationDeploy.value = 'ch';
      } else {
        resettableState.sceltaLocationDeploy.value = 'eu';
      }
    },
  );

  watch(resettableState.path, v => {
    if (v) {
      resettableState.fatturazioneAnnuale.value = !['cloud', 'host', 'storage'].includes(v);
    }

    if (v !== 'cloud') {
      resettableState.sceltaLocationBackup.value = resettableState.sceltaLocationDeploy.value;
    }

    if (v === 'cloud' && resettableState.db.value === 'best') {
      resettableState.db.value = 'mariadb';
    }

    resettableState.tecnologie.value = [];
  });

  watch(askDomainFirst, v => {
    resettableState.attivaDominio.value = v;
  });

  watch(resettableState.sceltaLocationDeploy, v => {
    if (resettableState.path.value !== 'cloud') {
      resettableState.sceltaLocationBackup.value = v;
    }
  });

  function getProdotto(codice: string): Product {
    // Il codice può essere un group_code (valido per entrambe le durate) o il codice di un prodotto specifico
    const prodotto =
      catalog.products.find(p => p.group_code === codice && p.durata === durata.value) ?? catalog.products.find(p => p.codice === codice);

    if (prodotto) {
      const eur = customer.value?.currency === 'EUR';
      const price = resettableState.fatturaMe.value ? prodotto : priceList.value.find(p => p.id === prodotto.id);

      if (price) {
        // Copia: gli oggetti del catalogo sono condivisi e non vanno modificati con i prezzi del cliente
        return {
          ...(prodotto as Product),
          prezzo: eur ? price.prezzo_eur : price.prezzo_chf,
          prezzo_scontato: eur ? price.prezzo_scontato_eur : price.prezzo_scontato_chf,
          prezzo_customer: login.currency == 'EUR' ? price.prezzo_clienti_eur : price.prezzo_clienti_chf,
          codice_wizard: codice,
        };
      }
    }
    return { codice } as Product;
  }


  function getProdotti(codici: readonly string[]) {
    const prodotti = [];
    for (const codice of codici) {
      prodotti.push(getProdotto(codice));
    }
    return prodotti;
  }

  async function nextStep() {
    for (let i = steps.value.findIndex(x => x.$options.name === resettableState.currentStep.value) + 1; i < steps.value.length; i++) {
      const step = steps.value[i] as (typeof steps.value)[number];
      const enabled = step.isEnabled ?? toValue(step.$.exposed?.isEnabled);
      if (enabled && step.$options.name) {
        if (step.$options.name === SUMMARY_STEP) {
          await submit('save');
        }
        resettableState.currentStep.value = step.$options.name;

        const page = document.querySelector('.pf-v6-c-page__main');
        page?.scroll({
          top: 0,
          behavior: 'smooth',
        });

        return;
      }
    }
  }

  // Step attivi dal primo raggiungibile in poi, mostrati nell'indicatore di avanzamento
  const progressSteps = computed(() => {
    const start = Math.max(
      steps.value.findIndex(x => x.$options.name === initialStep.value),
      0,
    );
    return steps.value
      .slice(start)
      .filter(step => step.$options.name !== THANKYOU_STEP && (step.isEnabled ?? toValue(step.$.exposed?.isEnabled)))
      .map(step => step.$options.name)
      .filter(name => name !== undefined);
  });

  function previousStep() {
    for (let i = steps.value.findIndex(x => x.$options.name === resettableState.currentStep.value) - 1; i >= 0; i--) {
      const step = steps.value[i] as (typeof steps.value)[number];
      const enabled = step.isEnabled ?? toValue(step.$.exposed?.isEnabled);
      if (enabled && step.$options.name) {
        resettableState.currentStep.value = step.$options.name;

        const page = document.querySelector('.pf-v6-c-page__main');
        page?.scroll({
          top: 0,
          behavior: 'smooth',
        });
        return;
      }
    }
  }

  function resetWizard(dontAsk?: boolean) {
    const r = dontAsk || confirm($t('Sei sicuro di voler resettare tutte le tue scelte e tornare al primo step?'));
    if (r) {
      setDefault();
    }
  }

  function otherIteration() {
    setDefault(offerState);
    resettableState.currentStep.value = DRAFT_OFFER_INITIAL_STEP;
  }

  function minPrice(codici: string[]) {
    const prodotti = getProdotti(codici);
    return prodotti.reduce((a, b) => (a.prezzo_scontato < b.prezzo_scontato ? a : b));
  }

  function totaleSingoloWizardByDurata(durata: (typeof PRODOTTIANAGRAFICA_DURATA)[number], scontato: boolean, group?: string) {
    const products = group ? groupedProducts.value[group] : Object.values(groupedProducts.value).flat();

    return (
      products
        ?.filter(p => p.durata === durata)
        .reduce((t, p) => t + (resettableState.fatturaMe.value && scontato ? p.prezzo_scontato : p.prezzo) * (p.qt ?? 1), 0) ?? 0
    );
  }

  function attiva(value: boolean) {
    return value ? $t('Attiva') : $t('Non attiva');
  }

  function siNo(value: boolean) {
    return value ? $t('Sì') : $t('No');
  }

  function saveOffer() {
    if (!login.id_azienda || !customer.value || !resettableState.action.value) {
      return;
    }

    let id_cliente;
    if (!resettableState.fatturaMe.value) {
      if (!resettableState.idClienteFinale.value) {
        throw new Error('Nessun cliente finale selezionato');
      }
      id_cliente = resettableState.idClienteFinale.value;
    } else {
      id_cliente = login.id_azienda;
    }

    const data: Partial<Offer> & InfoSaveWizard = {
      title: resettableState.title.value ?? defaultTitle.value,
      prodotti: [],
      id_azienda: Number(id_cliente),
      currency: customer.value.currency,
    };

    if (isFirstIteration.value) {
      data.free_period = resettableState.freePeriod.value ? 30 : 0;
    } else {
      if (offer.value?.id) {
        data.id_offerta = offer.value.id;
        data.title = offer.value.titolo_offerta ?? defaultTitle.value;
      }
    }

    if (login.is_reseller && resettableState.freePeriod.value) {
      data.testing_mode = resettableState.renewAfterFreePeriod.value ? 'rinnova' : 'disdici';
    }

    for (const prodotto of products.value) {
      data.prodotti.push({
        codice: prodotto.codice,
        qt: prodotto.qt ?? 1,
        dominio: prodotto.dominio,
        os: prodotto.os,
        webserver: prodotto.webserver,
        provision_db: prodotto.provision_db,
        provision_php: prodotto.provision_php,
        newService: prodotto.newService,
        siteTransfer: prodotto.siteTransfer,
        mailTransfer: prodotto.mailTransfer,
        notForResale: login.is_reseller && resettableState.notForResale.value,
        provision_region: prodotto.provision_region,
        provision_backup_region: prodotto.provision_backup_region,
        tecnologie: prodotto.tecnologie,
      });
    }

    return new WizardOfferResource().save(data);
  }

  function concludeOffer(action: WizardAction) {
    if (!offer.value) {
      return;
    }

    return new WizardOfferResource().put(offer.value?.id, {
      id_azienda: offer.value.id_azienda,
      action: action,
      title: resettableState.title.value,
      note: resettableState.customNote.value.length
        ? resettableState.customNote.value.join('\n') + (resettableState.note.value ? `\n${resettableState.note.value}` : '')
        : resettableState.note.value,
    });
  }

  async function removeProducts(domain: string) {
    if (offer.value && confirm(`Sei sicuro di voler rimuovere tutti i prodotti associati al dominio ${domain} dall'offerta?`)) {
      deletingProducts.value = true;
      try {
        offer.value = await new WizardOfferResource().products(offer.value.id).delete(domain);
      } finally {
        deletingProducts.value = false;
      }
    }
  }

  async function confirmOffer() {
    if (!offer.value) {
      return;
    }

    if (!canConfirmOffer.value) {
      alert.add({
        title: $t("Non ha i permessi per confermare l'offerta"),
        type: 'danger',
        toast: true,
      });
      return;
    }

    if ((resettableState.fatturaMe.value && !login.complete) || (!resettableState.fatturaMe.value && !customer.value?.has_complete_billing_data)) {
      await concludeOffer('confirm');
      router.push({
        name: 'confirm_offer',
        params: { id: offer.value.id },
      });
      return;
    }

    if (confirm(`Sei sicuro di voler confermare l'offerta ${offer.value.titolo_offerta}?`)) {
      const data: OfferUpdate = {
        stato: 'confermata',
        title: resettableState.title.value,
        note: resettableState.customNote.value.length
          ? resettableState.customNote.value.join('\n') + (resettableState.note.value ? `\n${resettableState.note.value}` : '')
          : resettableState.note.value,
      };

      if (resettableState.cashback.value > 0) {
        data.cashback_amount = resettableState.cashback.value;
      }

      await new OffersResource().put(offer.value.id, data);
      router.push({ name: 'order_result', params: { id: offer.value.id }, query: { res: 0 } });
    }
  }

  async function submit(action: WizardAction | 'continue' | 'save') {
    if (sending.value) {
      return;
    }

    const isSave = action === 'save' || action === 'continue';
    if (isSave ? catalog.products.length === 0 : !offer.value?.id) {
      return;
    }

    resettableState.action.value = action;
    sending.value = true;

    try {
      if (isSave) {
        const response = await saveOffer();
        if (!response) {
          return;
        }
        offer.value = response.offerta;

        resettableState.title.value = offer.value.titolo_offerta;

        if (action === 'continue') {
          otherIteration();
        } else {
          setDefault(offerState);
          resettableState.currentStep.value = 'WizardStepSummary';
        }
      } else if (offer.value) {
        await concludeOffer(action);

        if (action == 'confirm') {
          router.push({
            name: 'confirm_offer',
            params: { id: offer.value.id },
          });
        } else {
          nextStep();
        }
      }
    } finally {
      sending.value = false;
    }
  }


  catalog.init();
  setDefault();

  const declaredInstance: ComponentExposed<WizardInjection> = {
    ...resettableState,
    stacks,
    customers,
    customersState,
    customer,
    hostingPaths,
    cloudPaths,
    resellerPaths,
    onHostingPath,
    askClienti,
    modal,
    modalBoxTitle,
    modalFooter,
    offer,
    isFirstIteration,
    standardClouds,
    customOfferNotes,
    sending,
    opzioniProdottiInfrastruttura,
    codiciProdottiInfrastruttura,
    opzioniWebServers,
    codiciCloud,
    codiceProdottoStaging,
    codiceProdottoBackup,
    codiceProdottoSpazioBackup,
    codiciProdottiSql,
    codiciProdottiAddon,
    opzioniTecnologieHosting,
    opzioniTecnologieCloud,
    locations,
    cpuFirewallQt,
    ramFirewallQt,
    storageFirewallQt,
    durata,
    askOs,
    askEssentialOs,
    askCockpitConfig,
    askRegion,
    askRegionBackup,
    askProdotti,
    askStorageQt,
    domainRequired,
    domainRegistrationRequired,
    askDomainFirst,
    askDomainAddon,
    askSsl,
    askAddonCpanel,
    askAddonWindows,
    askStack,
    askFirewall,
    askNomeServer,
    askDb,
    askPhp,
    askTecnologie,
    askConfigurazione,
    askTest,
    askMailAddon,
    askDurataFatturazione,
    askSiteTransfer,
    askMailTransfer,
    askExtraTimeTransfer,
    askAddonSla,
    askAddonStorage,
    cloudOperativeSystems,
    hidePlusVat,
    dominioRegistrabile,
    sottodominio,
    codiceProdottoDominio,
    codiceProdottoImunify,
    canConfirmOffer,
    defaultTitle,
    configurazione,
    serverCloud,
    serverCloudName,
    firewall,
    firewallName,
    groupedProducts,
    totaliWizard,
    availableDomain: availableDomain,
    searchedDomain: searchedDomain,
    initialStep,
    progressSteps,
    getProdotto,
    getProdotti,
    nextStep,
    previousStep,
    resetWizard,
    otherIteration,
    minPrice,
    totaleSingoloWizardByDurata,
    attiva,
    siNo,
    removeProducts,
    confirmOffer,
    submit,
    tldRegistrabile,
  };

  provide(WizardInjectionKey, reactive(declaredInstance) as unknown as WizardInjection);

  return {
    ...declaredInstance,
    modalStyles,
    login,
    steps,
    catalog,
    priceList,
    ready,
    newOfferInitialStep,
    deletingProducts,
    products,
    setDefault,
    saveOffer,
    concludeOffer,
  };
}
